import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './mypage.scss';
import { AccountNav } from './AccountNav';
import {
  AccountUser, Engagement, HANDLE_REGEX, instruments, ProfileData, Recording, recordingKindLabel, RecordingKind,
  todayString, Visibility, visibilityLabel, Work, workStatusLabel, WorkStatus,
} from './profileTypes';

// 목록 행에 붙이는 화면용 key (서버로는 보내지 않음)
type Row<T> = T & { key: number };

type Errors = Record<string, string>;

const emptyWork: Work = { catalog: '', composer: '', title: '', status: 'played' };
const emptyEngagement: Engagement = { date: '', venue: '', title: '', role: '' };
const emptyRecording: Recording = { title: '', url: '', kind: 'live', date: '', duration: '' };

const URL_REGEX = /^https?:\/\/\S+$/i;
const DURATION_REGEX = /^(\d{1,2}:)?\d{1,2}:\d{2}$/;

const blankProfile: Omit<ProfileData, 'works' | 'engagements' | 'recordings'> = {
  handle: '',
  nameLat: '',
  instrument: '',
  ensemble: '',
  tagline: '',
  bio: '',
  teachers: '',
  instrumentDetail: '',
  availConcerto: false,
  availChamber: false,
  availLesson: false,
  availRegion: '',
  availFrom: '',
  visibility: 'link',
};

const isBlankWork = (w: Work) => !w.catalog.trim() && !w.composer.trim() && !w.title.trim();
const isBlankEngagement = (e: Engagement) => !e.date && !e.venue.trim() && !e.title.trim() && !e.role.trim();
const isBlankRecording = (r: Recording) => !r.title.trim() && !r.url.trim() && !r.date && !r.duration.trim();

export default function ProfileEdit() {

  const navigate = useNavigate();
  const nextKey = useRef(0);
  const withKey = <T,>(item: T): Row<T> => ({ ...item, key: nextKey.current++ });

  const [ready, setReady] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [form, setForm] = useState(blankProfile);
  const [works, setWorks] = useState<Row<Work>[]>([]);
  const [engagements, setEngagements] = useState<Row<Engagement>[]>([]);
  const [recordings, setRecordings] = useState<Row<Recording>[]>([]);
  const [handleMsg, setHandleMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [statusMsg, setStatusMsg] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // 로그인 확인 후 기존 프로필이 있으면 불러와서 수정 모드로
  useEffect(() => {
    (async () => {
      let user: AccountUser;
      try {
        user = (await axios.get('/api/auth/me')).data.user;
      } catch {
        navigate('/login', { replace: true });
        return;
      }
      try {
        const profile: ProfileData | null = (await axios.get('/api/profile/me')).data.profile;
        if (profile) {
          const { works: w, engagements: e, recordings: r, updatedAt, ...rest } = profile;
          setForm(rest);
          setWorks(w.map(withKey));
          setEngagements(e.map(withKey));
          setRecordings(r.length ? r.map(withKey) : [withKey(emptyRecording)]);
          setIsNew(false);
        } else {
          setForm((prev) => ({ ...prev, instrument: user.instrument || '' }));
          setWorks([withKey(emptyWork), withKey(emptyWork), withKey(emptyWork)]);
          setEngagements([withKey(emptyEngagement)]);
          setRecordings([withKey(emptyRecording)]);
        }
        setReady(true);
      } catch {
        setStatusMsg('프로필을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = async () => {
    await axios.post('/api/auth/logout').catch(() => {});
    navigate('/');
  };

  const setField = <K extends keyof typeof form>(name: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateWork = (key: number, patch: Partial<Work>) => {
    setWorks((prev) => prev.map((w) => (w.key === key ? { ...w, ...patch } : w)));
  };
  const updateEngagement = (key: number, patch: Partial<Engagement>) => {
    setEngagements((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  };
  const updateRecording = (key: number, patch: Partial<Recording>) => {
    setRecordings((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const checkHandle = async () => {
    const handle = form.handle.trim().toLowerCase();
    setField('handle', handle);
    try {
      const res = await axios.get('/api/profile/handle-check', { params: { handle } });
      setHandleMsg({ ok: res.data.available, text: res.data.message });
    } catch {
      setHandleMsg({ ok: false, text: '확인하지 못했습니다. 잠시 후 다시 시도해 주세요.' });
    }
  };

  const validate = (filledWorks: Row<Work>[], filledEngagements: Row<Engagement>[], filledRecordings: Row<Recording>[]): Errors => {
    const next: Errors = {};
    if (isNew && !HANDLE_REGEX.test(form.handle.trim().toLowerCase())) {
      next.handle = '영문 소문자·숫자·. _ - 로 3~30자, 처음과 끝은 영문이나 숫자여야 합니다.';
    }
    if (!form.instrument.trim()) next.instrument = '악기를 선택해 주세요.';
    filledWorks.forEach((w, i) => {
      if (!w.composer.trim() || !w.title.trim()) next[`works.${i}`] = '작곡가와 곡명을 입력해 주세요.';
    });
    filledEngagements.forEach((e, i) => {
      if (!e.date || !e.venue.trim() || !e.title.trim()) next[`engagements.${i}`] = '날짜, 장소, 공연명을 입력해 주세요.';
    });
    filledRecordings.forEach((r, i) => {
      if (!r.title.trim()) next[`recordings.${i}`] = '제목을 입력해 주세요.';
      else if (!URL_REGEX.test(r.url.trim())) next[`recordings.${i}`] = '링크를 https://로 시작하는 주소로 입력해 주세요.';
      else if (r.duration.trim() && !DURATION_REGEX.test(r.duration.trim())) next[`recordings.${i}`] = '길이는 14:32 형식으로 입력해 주세요.';
    });
    return next;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // 완전히 빈 행은 저장하지 않는다
    const filledWorks = works.filter((w) => !isBlankWork(w));
    const filledEngagements = engagements.filter((en) => !isBlankEngagement(en));
    setWorks(filledWorks.length ? filledWorks : [withKey(emptyWork)]);
    setEngagements(filledEngagements.length ? filledEngagements : [withKey(emptyEngagement)]);
    const filledRecordings = recordings.filter((r) => !isBlankRecording(r));
    setRecordings(filledRecordings.length ? filledRecordings : [withKey(emptyRecording)]);

    const next = validate(filledWorks, filledEngagements, filledRecordings);
    setErrors(next);
    setStatusMsg('');
    if (Object.keys(next).length > 0) {
      setStatusMsg('입력 내용을 확인해 주세요.');
      return;
    }

    setIsSaving(true);
    try {
      await axios.put('/api/profile/me', {
        ...form,
        handle: form.handle.trim().toLowerCase(),
        works: filledWorks.map(({ key, ...w }) => w),
        engagements: filledEngagements.map(({ key, ...en }) => en),
        recordings: filledRecordings.map(({ key, ...r }) => ({ ...r, url: r.url.trim(), duration: r.duration.trim() })),
      });
      navigate('/mypage');
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : undefined;
      if (data?.field) setErrors({ [data.field]: data.message });
      setStatusMsg(data?.message || '잠시 후 다시 시도해 주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  const today = todayString();

  return (
    <div className="account-page">
      <AccountNav onLogout={handleLogout} />

      <main className="wrap acc-main">
        <p className="eyebrow">{isNew ? 'New profile' : 'Edit profile'}</p>
        <h1>{isNew ? <>여섯 칸, <em>첫 곡부터</em>.</> : <>프로필 <em>수정</em></>}</h1>
        <p className="lede">모든 칸은 나중에 채워도 됩니다. 주소와 악기만 정하면 프로필이 만들어지고, 레퍼토리 열 곡부터 링크가 쓸 만해집니다.</p>

        {!ready && statusMsg && <p className="status-msg err">{statusMsg}</p>}

        {ready && (
          <form className="pf-form" onSubmit={handleSubmit} noValidate>

            {/* ---------------- 01 basic ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">01</span><h2>기본 정보</h2><span>필수</span></div>

              <div className="field">
                <span className="field-label">프로필 주소</span>
                {isNew ? (
                  <>
                    <div className="handle-row">
                      <span className="handle-pre">classicker.com/</span>
                      <input
                        type="text"
                        placeholder="yoon.seoha"
                        maxLength={30}
                        value={form.handle}
                        onChange={(e) => { setField('handle', e.target.value); setHandleMsg(null); }}
                        aria-invalid={!!errors.handle}
                      />
                      <button className="btn btn-ghost btn-sm" type="button" onClick={checkHandle}>중복 확인</button>
                    </div>
                    <span className="field-hint">한 번 정하면 바뀌지 않습니다.</span>
                    {handleMsg && <span className={handleMsg.ok ? 'field-ok' : 'field-err'}>{handleMsg.text}</span>}
                  </>
                ) : (
                  <div className="handle-fixed">classicker.com/{form.handle}</div>
                )}
                {errors.handle && <span className="field-err">{errors.handle}</span>}
              </div>

              <div className="field-grid">
                <label className="field">
                  <span className="field-label">악기 · 분야</span>
                  <select value={form.instrument} onChange={(e) => setField('instrument', e.target.value)} aria-invalid={!!errors.instrument}>
                    <option value="">선택해 주세요</option>
                    {/* 목록에 없는 기존 값도 그대로 보여준다 */}
                    {form.instrument && !instruments.includes(form.instrument) && <option value={form.instrument}>{form.instrument}</option>}
                    {instruments.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                  {errors.instrument && <span className="field-err">{errors.instrument}</span>}
                </label>
                <label className="field">
                  <span className="field-label">소속 앙상블 · 학교<span className="field-opt">선택</span></span>
                  <input type="text" maxLength={100} placeholder="앙상블 오르페우스" value={form.ensemble} onChange={(e) => setField('ensemble', e.target.value)} />
                </label>
                <label className="field">
                  <span className="field-label">영문 이름<span className="field-opt">선택</span></span>
                  <input type="text" maxLength={60} placeholder="YOON SEOHA" value={form.nameLat} onChange={(e) => setField('nameLat', e.target.value)} />
                </label>
                <label className="field">
                  <span className="field-label">한 줄 소개<span className="field-opt">선택</span></span>
                  <input type="text" maxLength={120} placeholder="악보의 여백을 소리로 읽는 연주자" value={form.tagline} onChange={(e) => setField('tagline', e.target.value)} />
                </label>
              </div>
            </section>

            {/* ---------------- 02 repertoire ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">02</span><h2>레퍼토리</h2><span>{works.filter((w) => !isBlankWork(w)).length}곡</span></div>
              <p className="fsec-desc">연주할 수 있는 곡을 쌓습니다. 상태로 “무대에 올릴 수 있음”과 “지금 공부 중”을 구분하세요.</p>

              <div className="rows">
                <div className="row-head works-cols">
                  <span>카탈로그</span><span>작곡가</span><span>곡명</span><span>상태</span><span></span>
                </div>
                {works.map((w, i) => (
                  <div key={w.key}>
                    <div className="row works-cols">
                      <input type="text" maxLength={40} placeholder="BWV 1004" aria-label="카탈로그 번호" value={w.catalog} onChange={(e) => updateWork(w.key, { catalog: e.target.value })} />
                      <input type="text" maxLength={60} placeholder="Bach" aria-label="작곡가" value={w.composer} onChange={(e) => updateWork(w.key, { composer: e.target.value })} />
                      <input type="text" maxLength={160} placeholder="Partita No. 2 — Chaconne" aria-label="곡명" value={w.title} onChange={(e) => updateWork(w.key, { title: e.target.value })} />
                      <select aria-label="상태" value={w.status} onChange={(e) => updateWork(w.key, { status: e.target.value as WorkStatus })}>
                        {(Object.keys(workStatusLabel) as WorkStatus[]).map((s) => <option key={s} value={s}>{workStatusLabel[s]}</option>)}
                      </select>
                      <button className="row-del" type="button" aria-label="삭제" onClick={() => setWorks((prev) => prev.filter((x) => x.key !== w.key))}>×</button>
                    </div>
                    {errors[`works.${i}`] && <span className="field-err">{errors[`works.${i}`]}</span>}
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm add-row" type="button" onClick={() => setWorks((prev) => [...prev, withKey(emptyWork)])}>+ 곡 추가</button>
            </section>

            {/* ---------------- 03 engagements ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">03</span><h2>연주 이력 · 이번 시즌</h2><span>{engagements.filter((en) => !isBlankEngagement(en)).length}건</span></div>
              <p className="fsec-desc">지난 무대와 다가오는 무대를 함께 넣습니다. 오늘 이후 날짜는 프로필 맨 위 ‘이번 시즌’에 표시됩니다.</p>

              <div className="rows">
                <div className="row-head eng-cols">
                  <span>날짜</span><span>장소</span><span>공연명</span><span>역할</span><span></span>
                </div>
                {engagements.map((en, i) => (
                  <div key={en.key}>
                    <div className={`row eng-cols${en.date && en.date >= today ? ' upcoming' : ''}`}>
                      <input type="date" aria-label="날짜" value={en.date} onChange={(e) => updateEngagement(en.key, { date: e.target.value })} />
                      <input type="text" maxLength={100} placeholder="금호아트홀 연세" aria-label="장소" value={en.venue} onChange={(e) => updateEngagement(en.key, { venue: e.target.value })} />
                      <input type="text" maxLength={160} placeholder="독주회" aria-label="공연명" value={en.title} onChange={(e) => updateEngagement(en.key, { title: e.target.value })} />
                      <input type="text" maxLength={60} placeholder="독주 · 제1바이올린" aria-label="역할" value={en.role} onChange={(e) => updateEngagement(en.key, { role: e.target.value })} />
                      <button className="row-del" type="button" aria-label="삭제" onClick={() => setEngagements((prev) => prev.filter((x) => x.key !== en.key))}>×</button>
                    </div>
                    {errors[`engagements.${i}`] && <span className="field-err">{errors[`engagements.${i}`]}</span>}
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm add-row" type="button" onClick={() => setEngagements((prev) => [...prev, withKey(emptyEngagement)])}>+ 무대 추가</button>
            </section>

            {/* ---------------- 04 recordings ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">04</span><h2>녹음 · 영상</h2><span>{recordings.filter((r) => !isBlankRecording(r)).length}개</span></div>
              <p className="fsec-desc">유튜브 등 외부 링크를 그대로 붙입니다. 프로그램을 보내기 전에 상대가 소리로 먼저 판단할 수 있습니다.</p>

              <div className="rows">
                <div className="row-head rec-cols">
                  <span>제목</span><span>링크</span><span>구분</span><span>날짜</span><span>길이</span><span></span>
                </div>
                {recordings.map((r, i) => (
                  <div key={r.key}>
                    <div className="row rec-cols">
                      <input type="text" maxLength={160} placeholder="바흐 · 샤콘느 (라이브)" aria-label="제목" value={r.title} onChange={(e) => updateRecording(r.key, { title: e.target.value })} />
                      <input type="text" inputMode="url" maxLength={500} placeholder="https://youtu.be/…" aria-label="링크" value={r.url} onChange={(e) => updateRecording(r.key, { url: e.target.value })} />
                      <select aria-label="구분" value={r.kind} onChange={(e) => updateRecording(r.key, { kind: e.target.value as RecordingKind })}>
                        {(Object.keys(recordingKindLabel) as RecordingKind[]).map((k) => <option key={k} value={k}>{recordingKindLabel[k]}</option>)}
                      </select>
                      <input type="date" aria-label="날짜" value={r.date} onChange={(e) => updateRecording(r.key, { date: e.target.value })} />
                      <input type="text" maxLength={8} placeholder="14:32" aria-label="길이" value={r.duration} onChange={(e) => updateRecording(r.key, { duration: e.target.value })} />
                      <button className="row-del" type="button" aria-label="삭제" onClick={() => setRecordings((prev) => prev.filter((x) => x.key !== r.key))}>×</button>
                    </div>
                    {errors[`recordings.${i}`] && <span className="field-err">{errors[`recordings.${i}`]}</span>}
                  </div>
                ))}
              </div>
              <button className="btn btn-ghost btn-sm add-row" type="button" onClick={() => setRecordings((prev) => [...prev, withKey(emptyRecording)])}>+ 녹음 추가</button>
            </section>

            {/* ---------------- 05 availability ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">05</span><h2>연주 가능 상태</h2><span>선택</span></div>
              <p className="fsec-desc">지금 받을 수 있는 일을 표시하면, 연락해도 되는 사람인지 상대가 먼저 판단할 수 있습니다.</p>

              <div className="checks">
                <label className="check"><input type="checkbox" checked={form.availConcerto} onChange={(e) => setField('availConcerto', e.target.checked)} /><span>협연</span></label>
                <label className="check"><input type="checkbox" checked={form.availChamber} onChange={(e) => setField('availChamber', e.target.checked)} /><span>실내악</span></label>
                <label className="check"><input type="checkbox" checked={form.availLesson} onChange={(e) => setField('availLesson', e.target.checked)} /><span>레슨</span></label>
              </div>
              <div className="field-grid">
                <label className="field">
                  <span className="field-label">지역</span>
                  <input type="text" maxLength={100} placeholder="서울 / 경기" value={form.availRegion} onChange={(e) => setField('availRegion', e.target.value)} />
                </label>
                <label className="field">
                  <span className="field-label">가능 시기</span>
                  <input type="month" value={form.availFrom} onChange={(e) => setField('availFrom', e.target.value)} aria-invalid={!!errors.availFrom} />
                  {errors.availFrom && <span className="field-err">{errors.availFrom}</span>}
                </label>
              </div>
            </section>

            {/* ---------------- 06 about ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">06</span><h2>소개와 악기</h2><span>선택</span></div>
              <div className="field-grid">
                <label className="field">
                  <span className="field-label">사사</span>
                  <input type="text" maxLength={200} placeholder="김OO, 이OO" value={form.teachers} onChange={(e) => setField('teachers', e.target.value)} />
                </label>
                <label className="field">
                  <span className="field-label">사용 악기</span>
                  <input type="text" maxLength={120} placeholder="G. B. Guadagnini, 1782" value={form.instrumentDetail} onChange={(e) => setField('instrumentDetail', e.target.value)} />
                </label>
              </div>
              <label className="field">
                <span className="field-label">소개</span>
                <textarea rows={5} maxLength={2000} placeholder="짧아도 됩니다. 학력, 수상, 주요 활동 등" value={form.bio} onChange={(e) => setField('bio', e.target.value)} />
              </label>
            </section>

            {/* ---------------- 07 visibility ---------------- */}
            <section className="fsec">
              <div className="fsec-head"><span className="mk">07</span><h2>공개 범위</h2></div>
              <div className="radios">
                {(Object.keys(visibilityLabel) as Visibility[]).map((v) => (
                  <label className={`radio${form.visibility === v ? ' on' : ''}`} key={v}>
                    <input type="radio" name="visibility" value={v} checked={form.visibility === v} onChange={() => setField('visibility', v)} />
                    <span>{visibilityLabel[v]}</span>
                    {v === 'link' && <small>기본값 · 검색에는 노출되지 않습니다</small>}
                  </label>
                ))}
              </div>
            </section>

            <div className="savebar">
              <p className={`status-msg${Object.keys(errors).length ? ' err' : ''}`} role="status">{statusMsg}</p>
              <Link className="btn btn-ghost" to="/mypage">취소</Link>
              <button className="btn btn-primary" type="submit" disabled={isSaving}>
                {isSaving ? '저장 중…' : isNew ? '프로필 만들기' : '저장하기'}
              </button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
